import { z } from "zod";

import { CalendarDateSchema, MONEY_LIMIT, hasAtMostTwoDecimals } from "./common.js";

/**
 * DTOs for the savings context.
 *
 * Same contract role as `identity.ts`: use cases return these, route handlers pass them through
 * unchanged, and `@gladis/api-client` derives its types from the same schemas — so a contract change
 * is a compile error on both sides at once.
 *
 * TWO REPRESENTATION DECISIONS ARE ENCODED HERE, AND BOTH ARE LOAD-BEARING.
 *
 * **Money crosses as a number of EUROS.** `12.5` is twelve euros fifty; never cents, never a string.
 * `formatEur` takes a number, so this is the shape the interface needs, and a single stored amount
 * is exactly representable — `numeric(15,2)` tops out at 9,999,999,999,999.99, where the gap between
 * doubles is about 0.002. What is NOT safe is adding these up: totals are computed as `Valuation`
 * (integer cents) and converted once, at the edge, with `Valuation.toEuros()`. See spec 006
 * research R4.
 *
 * **A snapshot's date crosses as `YYYY-MM-DD`, not an ISO timestamp.** `as_of_date` is a `date`
 * column — a calendar date with no time and no timezone — and it is the first one the application
 * reads. Turning it into a `Date` and asking that `Date` for its month shifts by a day whenever the
 * process runs behind UTC, which silently moves a balance into the previous month on the chart. As a
 * string it compares and buckets exactly. `recordedAt` is a real instant and stays a timestamp.
 */

export const SavingsOwnershipSchema = z.enum(["joint", "individual"]);
export type SavingsOwnershipValue = z.infer<typeof SavingsOwnershipSchema>;

/**
 * `YYYY-MM-DD`. Read the header before changing this to `z.iso.datetime()`.
 *
 * Moved to `common.ts` by feature 008, when Properties became the second context to need it. It is
 * re-exported here so every existing importer keeps working and the rule keeps exactly one
 * definition.
 */
export { CalendarDateSchema };

/**
 * How far back the chart looks. `12m` by default.
 *
 * A selector rather than a fixed window, because the two questions a household asks of this chart
 * are different: "how has the year gone" and "what happened this month". A twelve-month axis
 * flattens a fortnight of activity into two pixels.
 */
export const SavingsChartRangeSchema = z.enum(["1m", "3m", "12m"]);
export type SavingsChartRange = z.infer<typeof SavingsChartRangeSchema>;

/**
 * The magnitude a `numeric(15,2)` column holds.
 *
 * Stated here as well as in the domain because the schema is the outer edge: rejecting at the
 * boundary means the domain's own refusal (`SAV_BALANCE_OUT_OF_RANGE`) is a backstop rather than the
 * only guard. Negatives are permitted — a savings account can briefly go negative, and spec 006
 * FR-011 says so explicitly.
 */
export const BALANCE_LIMIT = MONEY_LIMIT;

export const BalanceSchema = z
  .number()
  .finite()
  .min(-BALANCE_LIMIT)
  .max(BALANCE_LIMIT)
  .refine(hasAtMostTwoDecimals, { message: "At most two decimal places." });

export const SavingsAccountDtoSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1).max(100),
  ownership: SavingsOwnershipSchema,
  /** Null when joint, and also once an individual owner's account has been deleted. */
  ownerId: z.uuid().nullable(),
  /**
   * Resolved server-side, never assembled by the client.
   *
   * The owner's CURRENT display name while they are an active member, falling back to the name
   * snapshotted on the account once they are not — which is what `owner_display_name` was added for
   * in 003 and what spec 006 FR-024 requires. The client cannot get the fallback wrong because it
   * never sees the choice.
   */
  ownerLabel: z.string().nullable(),
  /** The latest snapshot dated on or before today. Zero when there is none (FR-018). */
  currentBalance: BalanceSchema,
  archivedAt: z.iso.datetime().nullable(),
});
export type SavingsAccountDto = z.infer<typeof SavingsAccountDtoSchema>;

/**
 * One row of the header breakdown: the household's savings, split by who holds them.
 *
 * `key` is `"joint"` or a member's user id — stable, so the interface never keys on a display name
 * that two people could share. The rows SUM TO `total` exactly, which is the property that makes
 * the header readable: every euro is attributed to exactly one row.
 *
 * A joint account gets its own row rather than being divided between members. `savings.md` locks
 * ownership as informational and says a joint account "belongs to the household as a whole", so
 * splitting it would assert a contribution ratio the data does not record — and would silently stop
 * being true the moment a third member joined.
 */
export const SavingsBreakdownRowSchema = z.object({
  key: z.string(),
  /**
   * The holder's name, or `null` when the row is not about a NAMED person — feature 021.
   *
   * Two rows have no name, and `key` already tells them apart: `"joint"` is the household's own
   * money, anything else with a null label is an owner who has left and snapshotted nothing. Both
   * used to arrive as an English word composed in the use case, which no catalogue could reach.
   */
  label: z.string().nullable(),
  total: BalanceSchema,
});
export type SavingsBreakdownRow = z.infer<typeof SavingsBreakdownRowSchema>;

/**
 * `GET /api/savings/accounts`.
 *
 * `archived` is in the SAME response as `active` rather than behind a second endpoint, because
 * revealing archived accounts is a disclosure and not a filter (FR-037a): the total and the active
 * list must be identical whether or not they are shown, and returning both together makes that
 * structural instead of a rule someone has to remember.
 */
/**
 * How the household's savings have moved this month.
 *
 * `asOf` is the last day of the PREVIOUS month, and `change` is today's total minus the total on that day —
 * subtracted in integer cents on the server, so the client never takes one float from another. Both figures
 * are carried forward the way every balance in this module is: an account with no snapshot of its own by that
 * date resolves to its most recent earlier one, and to zero before its first.
 *
 * NULL WHEN THE HOUSEHOLD ITSELF WAS CREATED THIS MONTH — it has no previous month, and the change would
 * otherwise equal the whole total, which says when they started using GLADIS rather than what they saved. The
 * test is the household's creation month and deliberately not "the earliest snapshot": FR-027 already names
 * that as a defect, because a bound derived from the data moves when a backdated balance arrives.
 */
export const SavingsChangeDtoSchema = z.object({
  asOf: CalendarDateSchema,
  /** The household total on `asOf`. */
  total: BalanceSchema,
  /** Signed: negative when the household spent more than it put aside. */
  change: z.number(),
});
export type SavingsChangeDto = z.infer<typeof SavingsChangeDtoSchema>;

export const SavingsAccountsDtoSchema = z.object({
  /** Sum of `currentBalance` across `active` only (FR-019). */
  total: BalanceSchema,
  /** Joint first, then one row per member holding an individual account. Sums to `total`. */
  breakdown: z.array(SavingsBreakdownRowSchema),
  /** The month-on-month movement, or null when there is nothing before this month to compare with. */
  sinceLastMonth: SavingsChangeDtoSchema.nullable(),
  active: z.array(SavingsAccountDtoSchema),
  archived: z.array(SavingsAccountDtoSchema),
});
export type SavingsAccountsDto = z.infer<typeof SavingsAccountsDtoSchema>;

export const SavingsSnapshotDtoSchema = z.object({
  id: z.uuid(),
  balance: BalanceSchema,
  asOfDate: CalendarDateSchema,
  /** The instant it was recorded. Breaks ties when two snapshots share `asOfDate`. */
  recordedAt: z.iso.datetime(),
  recordedByLabel: z.string().nullable(),
  /**
   * Who recorded it, when that is still knowable (020).
   *
   * Null once they have deleted their account — `savings_balance_snapshots.recorded_by` is
   * `on delete set null` and no name is snapshotted beside it, which is why `recordedByLabel` goes
   * null at the same moment. That pair is the difference between a row that can show a face and one
   * that shows a generic stand-in and therefore no mark at all.
   */
  recordedById: z.uuid().nullable(),
});
export type SavingsSnapshotDto = z.infer<typeof SavingsSnapshotDtoSchema>;

/**
 * `GET /api/savings/accounts/:accountId`.
 *
 * Every snapshot, newest first, including any dated in the future — the history is the record, and
 * `currentBalance` on the account is the interpretation. The two disagreeing for a future-dated
 * snapshot is correct, not a bug.
 */
export const SavingsAccountDetailDtoSchema = z.object({
  account: SavingsAccountDtoSchema,
  snapshots: z.array(SavingsSnapshotDtoSchema),
});
export type SavingsAccountDetailDto = z.infer<typeof SavingsAccountDetailDtoSchema>;

/**
 * One point on a line: a DATE, not a month.
 *
 * The chart used to bucket by month, and that hid data. A household recording three balances in
 * July saw one point for July — the last of the three — so two thirds of what somebody had entered
 * was invisible on the chart meant to show it. Nine snapshots rendered as four points.
 *
 * Points now sit at the dates balances were actually recorded, so every entry is a vertex on the
 * line and nothing a member typed goes missing.
 */
export const SavingsChartPointSchema = z.object({
  at: CalendarDateSchema,
  value: BalanceSchema,
});

export const SavingsChartSeriesDtoSchema = z.object({
  /**
   * The account id, and the reason this field exists.
   *
   * Two accounts may share a name (FR-002), and keying chart layers by the label merges them
   * silently — a layer disappears and the top of the stack understates the household's money. The
   * server sends identity so the client cannot invent a key.
   */
  id: z.uuid(),
  label: z.string(),
  points: z.array(SavingsChartPointSchema),
});

/**
 * `GET /api/savings/chart`.
 *
 * Both window bounds are the server's, and the client derives no month of its own. The read model
 * already resolved which months exist under FR-027/028/029; a component that recomputed them from
 * its own clock could disagree across a month boundary, ask for a month that was never sent, and
 * have the carry-forward rule fill it with the previous month's value — a plausible-looking column
 * nobody computed.
 */
export const SavingsChartDtoSchema = z.object({
  /** The selected range, echoed back so the control reflects what was actually applied. */
  range: SavingsChartRangeSchema,
  windowStart: CalendarDateSchema,
  windowEnd: CalendarDateSchema,
  series: z.array(SavingsChartSeriesDtoSchema),
});
export type SavingsChartDto = z.infer<typeof SavingsChartDtoSchema>;

/**
 * `POST /api/savings/accounts`.
 *
 * The opening balance is a snapshot, so every balance rule applies to it in full.
 *
 * The date's UPPER bound — no more than ten years ahead — is deliberately NOT here. It depends on
 * today, and a schema evaluated at import time has no clock; putting it here would either freeze
 * "today" at module load or reach for `new Date()` in a place that cannot be given a fixed one. The
 * domain owns that check, where the `Clock` port is injected and a test can fix it. The lower bound
 * is absolute, so it stays.
 */
export const CreateSavingsAccountRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    ownership: SavingsOwnershipSchema,
    ownerId: z.uuid().nullable().optional(),
    openingBalance: BalanceSchema,
    asOfDate: CalendarDateSchema.refine((value) => value >= "1900-01-01", {
      message: "Date is implausibly far in the past.",
    }),
  })
  .refine((body) => body.ownership === "joint" || body.ownerId != null, {
    message: "An individual account needs an owner.",
    path: ["ownerId"],
  })
  .refine((body) => body.ownership === "individual" || body.ownerId == null, {
    message: "A joint account has no owner.",
    path: ["ownerId"],
  });
export type CreateSavingsAccountRequest = z.infer<typeof CreateSavingsAccountRequestSchema>;

/** `POST /api/savings/accounts/:accountId/balances`. Appends; never replaces. */
export const RecordBalanceRequestSchema = z.object({
  balance: BalanceSchema,
  asOfDate: CalendarDateSchema.refine((value) => value >= "1900-01-01", {
    message: "Date is implausibly far in the past.",
  }),
});
export type RecordBalanceRequest = z.infer<typeof RecordBalanceRequestSchema>;
